import { AfterViewInit, Component, OnDestroy, ChangeDetectionStrategy, effect, inject, output, input, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { VariableScope } from '@app/services/variable.service';
import { SbTextConditionService, TextConditionContext } from './sb-text-condition.service';
import { blockAt } from './text-condition-grammar';

export type SbTextConditionTarget = HTMLInputElement | HTMLTextAreaElement | string | null;

/** Events after which the caret may have moved into or out of a block. */
const CARET_EVENTS = ['keyup', 'click', 'select', 'input', 'focus'];

/**
 * The "Condition" button beside a plain-text field's variable button. It writes
 * `{% if … %}…{% endif %}` around the selection, or edits the block the caret is in. Hidden when
 * the server offers no text conditions.
 */
@Component({
  selector: 'app-sb-text-condition-button',
  templateUrl: './sb-text-condition-button.component.html',
  styleUrls: ['./sb-text-condition-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbTextConditionButtonComponent implements AfterViewInit, OnDestroy {
  private readonly conditions = inject(SbTextConditionService);

  /** The field: its element (a template reference), or its id. */
  readonly target = input<SbTextConditionTarget>(null);
  readonly disabled = input(false);
  /** Icon only, for a button inside the field (sb-input's companions). */
  readonly iconOnly = input(false);
  /** The variables the field can use: 'systemic' for a system email's field. */
  readonly variableScope = input<VariableScope>('campaign');
  /** The field's new text after the dialog changed it. */
  readonly applied = output<string>();

  readonly context = signal<TextConditionContext | null>(null);
  /** The caret is inside a block: the button edits it. */
  readonly insideBlock = signal(false);
  readonly busy = signal(false);
  private field: HTMLInputElement | HTMLTextAreaElement | null = null;
  private subscription = new Subscription();
  private viewInitialized = false;
  private readonly onCaret = () => this.updateCaret();

  constructor() {
    // Each scope has its own variables: the first loads with the first check, a new one on change.
    effect(() => {
      const scope = this.variableScope();
      untracked(() => this.load(scope));
    });
    // A field handed in later is bound at once; the first is bound after the view (ngAfterViewInit).
    effect(() => {
      this.target();
      untracked(() => {
        if (this.viewInitialized) {
          this.bind();
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    // An id may name an element its owner renders in the same pass.
    setTimeout(() => this.bind());
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.unbind();
  }

  get label(): string {
    return this.insideBlock() ? 'Edit the condition around the cursor' : 'Show text only when… (add a condition)';
  }

  get isDisabled(): boolean {
    const field = this.resolve();
    return this.disabled() || this.busy() || !field || field.disabled || field.readOnly;
  }

  async open(): Promise<void> {
    const field = this.resolve();
    if (!field || this.isDisabled) {
      return;
    }
    const length = (field.value || '').length;
    const start = field.selectionStart ?? length;
    const end = field.selectionEnd ?? start;
    this.busy.set(true);
    try {
      const next = await this.conditions.edit(field, start, end, this.variableScope());
      if (next !== null) {
        this.applied.emit(next);
      }
    } finally {
      this.busy.set(false);
      this.updateCaret();
    }
  }

  private load(scope: VariableScope): void {
    this.subscription.unsubscribe();
    // Never check the text against the other scope's list while this one loads.
    this.context.set(null);
    this.subscription = this.conditions.context(scope).subscribe((context) => {
      this.context.set(context);
      this.updateCaret();
    });
  }

  private resolve(): HTMLInputElement | HTMLTextAreaElement | null {
    const target = this.target();
    if (typeof target === 'string') {
      return (document.getElementById(target) as HTMLInputElement | HTMLTextAreaElement) || null;
    }
    return target || null;
  }

  private bind(): void {
    this.unbind();
    this.field = this.resolve();
    CARET_EVENTS.forEach((name) => this.field?.addEventListener(name, this.onCaret));
    this.updateCaret();
  }

  private unbind(): void {
    CARET_EVENTS.forEach((name) => this.field?.removeEventListener(name, this.onCaret));
    this.field = null;
  }

  private updateCaret(): void {
    const field = this.field || this.resolve();
    const context = this.context();
    if (!field || !context) {
      this.insideBlock.set(false);
      return;
    }
    const start = field.selectionStart ?? 0;
    const end = field.selectionEnd ?? start;
    this.insideBlock.set(!!blockAt(this.conditions.analyze(field.value || '', context), start, end));
  }
}
